import React, { useEffect, useState, useCallback, useMemo } from 'react';
import type { SavedCustomFilter } from '../../../services/quote.service';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import Toggle from '../../../components/Toggle';
import { FilterConditionRow, FilterCondition } from '../../../components/FilterConditionRow';
import { formatFilterValueForDisplay } from '../../../utils/dynamicColumns';
import { FieldDefinition } from '../../../contexts/FieldDefinitionsContext';

/*
 * Custom Filter — FirstCardModal-style condition builder, scope-agnostic.
 *
 * The builder itself (rows, operators, save/load, apply) is identical for every
 * scope; what differs is WHERE the field registry and the saved filters come
 * from. Those are props, so the quotes dialog (CustomFilterDialog) and the
 * templates dialog (TemplateCustomFilterDialog) are thin wrappers over this and
 * can never diverge in behaviour.
 *
 * Fields are DB/server-driven (quotes: GET /field-definitions?scope=quotes;
 * templates: GET /templates/filter-fields); operators/value inputs are the
 * shared FilterConditionRow (same UI the Dispatch/LC dashboards use). On apply
 * it emits a JSON FilterCondition[] string the backend translator
 * (build_quote_custom_filter / build_template_custom_filter) turns into SQL.
 * Conditions are AND-ed (no per-row OR); users combine values within a field via
 * comma / multi-select.
 *
 * The legacy V1 `~~`/`|||` blob is still accepted by the backend for filters
 * saved before the cutover, but the editor only round-trips the JSON format.
 */

/** Serialize the builder rows to the JSON FilterCondition[] the backend expects
 *  (drops the client-only `id` and any row without a field). Empty -> ''. */
export function buildFilterJson(conditions: FilterCondition[]): string {
  const used = conditions
    .filter((c) => c.field && c.field.trim())
    .map((c) => ({ field: c.field, compare: c.compare, values: c.values }));
  return used.length ? JSON.stringify(used) : '';
}

/** Parse a stored filter string back into builder rows. JSON -> conditions;
 *  a legacy blob (or empty) -> [] (not editable in the new UI). */
export function parseFilterJson(raw: string | undefined): FilterCondition[] {
  const s = (raw || '').trim();
  if (!s.startsWith('[')) return [];
  try {
    const arr = JSON.parse(s);
    if (!Array.isArray(arr)) return [];
    return arr.map((c: any, i: number) => ({
      id: i,
      field: String(c?.field ?? ''),
      compare: String(c?.compare ?? ''),
      values: String(c?.values ?? ''),
    }));
  } catch {
    return [];
  }
}

/** Operator -> short human phrasing for the active-filter summary chips. */
const OP_PHRASE: Record<string, string> = {
  'Equal to': 'is', 'Not equal to': 'is not',
  'Contains': 'contains', 'Does not contain': 'excludes',
  'Starts with': 'starts with', 'Does not start with': "doesn't start with",
  'Ends with': 'ends with', 'Does not end with': "doesn't end with",
  'Greater than': '>', 'Greater than or equal to': '≥',
  'Less than': '<', 'Less than or equal to': '≤', 'Between': 'between',
  'Blank or after': 'blank or >', 'Blank or before': 'blank or <',
  'Is empty': 'is blank', 'Is not empty': 'is not blank',
};
const _NO_VALUE_OPS = new Set(['Is empty', 'Is not empty']);

/** Human-readable one-liner per condition for the active-filter banner. Maps each
 *  field to its registry displayname; falls back to the raw fieldname. */
export function summarizeFilter(filter: string, fieldDefinitions: FieldDefinition[]): string[] {
  return parseFilterJson(filter)
    .filter((c) => c.field)
    .map((c) => {
      const label = fieldDefinitions.find((f) => f.fieldname === c.field)?.displayname ?? c.field;
      const phrase = OP_PHRASE[c.compare] ?? c.compare.toLowerCase();
      if (_NO_VALUE_OPS.has(c.compare)) return `${label} ${phrase}`;
      // Relative dates are stored as @tokens — show their human labels here.
      const val = formatFilterValueForDisplay(c.values ?? '').replace(/\s*,\s*/g, ', ');
      return val ? `${label} ${phrase} ${val}` : `${label} ${phrase} …`;
    });
}

/** Canonical JSON for a stored filter — round-trips through the builder so a
 *  ported filter (pretty-printed JSON) compares equal to a freshly-built one
 *  (drives the "edited / unsaved" comparison + saved-filter matching). */
function canon(raw?: string): string {
  return buildFilterJson(parseFilterJson(raw));
}

const EMPTY: FilterCondition = { id: 0, field: '', compare: '', values: '' };

/** Hard stop on how many rows the builder will add — mirrors the backend
 *  _FILTER_CONDITION_CAP — the server truncates past this, which would silently
 *  widen results (the dropped conditions were AND-ed, so a truncated filter
 *  returns MORE rows than the user built, with nothing on screen saying so).
 *  Every scope's backend has the same cap, so the guard lives in the shared base. */
const MAX_CONDITIONS = 25;

interface BaseProps {
  open: boolean;
  onClose: () => void;
  /** Current filter string (JSON FilterCondition[]); legacy blobs aren't editable here. */
  initialBlob?: string;
  /** `name` is the saved-filter name when the applied filter came from one
   *  (unmodified). `includeDeleted` is only meaningful to a scope that opted into the
   *  toggle below; it is always false for the others. */
  onApply: (filter: string, name?: string, includeDeleted?: boolean) => void;
  /** Opt-in "Include Deleted" toggle, for a scope whose default excludes
   *  soft-deleted records. Off for Quotes, which has no such notion. */
  showIncludeDeleted?: boolean;
  /** Prefill for that toggle, so re-opening the dialog to edit a running filter
   *  doesn't silently reset the scope it was applied with. */
  initialIncludeDeleted?: boolean;
  /** The scope's filter field registry (server-owned; the wrapper fetches it). */
  fieldDefinitions: FieldDefinition[];
  loadingFields?: boolean;
  /** The current user's saved filters for this scope. */
  listFilters: () => Promise<SavedCustomFilter[]>;
  /** Upsert a named filter for this scope (by name). */
  saveFilter: (name: string, filter: string) => Promise<unknown>;
}

/** The public surface every scope's wrapper exposes — the base's props minus the
 *  scope bindings the wrapper supplies. Both wrappers use this so the caller-facing
 *  interface is declared once. */
export type CustomFilterDialogProps = Pick<
  BaseProps, 'open' | 'onClose' | 'initialBlob' | 'onApply'
  | 'showIncludeDeleted' | 'initialIncludeDeleted'
>;

const CustomFilterDialogBase: React.FC<BaseProps> = ({
  open,
  onClose,
  initialBlob,
  onApply,
  showIncludeDeleted = false,
  initialIncludeDeleted = false,
  fieldDefinitions,
  loadingFields = false,
  listFilters,
  saveFilter,
}) => {
  // Only offer filterable fields in the condition builder — hides the deprecated
  // (direct)/(whse) lane duplicates (is_filterable=false), now covered by the unified
  // search fields. (The backend translator already ignores non-filterable conditions.)
  const filterableFieldDefs = useMemo(
    () => fieldDefinitions.filter((d) => d.is_filterable !== false),
    [fieldDefinitions],
  );
  const [conditions, setConditions] = useState<FilterCondition[]>([EMPTY]);
  const [idCounter, setIdCounter] = useState(1);
  const [saved, setSaved] = useState<SavedCustomFilter[]>([]);
  const [saveName, setSaveName] = useState('');
  // Scope widener, only rendered when the host opts in (showIncludeDeleted).
  const [includeDeleted, setIncludeDeleted] = useState(initialIncludeDeleted);
  // The saved filter currently loaded (name + its canonical JSON), or null. Kept
  // across edits so we can show an "edited / unsaved" indicator (dirty = current
  // conditions differ from loaded.filter) and offer "Update <name>".
  const [loaded, setLoaded] = useState<{ name: string; filter: string } | null>(null);

  useEffect(() => {
    if (!open) return;
    const parsed = parseFilterJson(initialBlob);
    setConditions(parsed.length ? parsed : [{ ...EMPTY }]);
    setIdCounter(parsed.length || 1);
    setSaveName('');
    setLoaded(null);
    // Re-open must show the scope the running filter was applied with, not a
    // stale one from the previous open.
    setIncludeDeleted(initialIncludeDeleted);
    listFilters().then((list) => {
      setSaved(list);
      // If the active filter matches a saved one (compared canonically), surface it.
      const target = canon(initialBlob);
      const match = target ? list.find((f) => canon(f.filter) === target) : undefined;
      if (match) setLoaded({ name: match.name, filter: canon(match.filter) });
    }).catch(() => setSaved([]));
    // `listFilters` is deliberately NOT a dep: wrappers pass a fresh arrow on
    // every render, so including it would refetch in an endless loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBlob, initialIncludeDeleted]);

  const addCondition = useCallback(() => {
    setConditions((prev) => [...prev, { id: idCounter, field: '', compare: '', values: '' }]);
    setIdCounter((n) => n + 1);
  }, [idCounter]);

  const removeCondition = useCallback((id: number) => {
    setConditions((prev) => (prev.length > 1 ? prev.filter((c) => c.id !== id) : [{ ...EMPTY }]));
  }, []);

  const updateCondition = useCallback((id: number, field: keyof FilterCondition, value: string) => {
    setConditions((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        // Changing the field resets operator + value (operators/value input are field-typed).
        if (field === 'field') return { ...c, field: value, compare: '', values: '' };
        return { ...c, [field]: value };
      })
    );
  }, []);

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open) return null;

  const atCap = conditions.length >= MAX_CONDITIONS;
  const currentJson = buildFilterJson(conditions);
  // "Edited but unsaved": a saved filter is loaded and the conditions have diverged.
  const dirty = !!loaded && currentJson !== loaded.filter;

  // Pass the saved name only when the applied filter is unchanged from it.
  const apply = () => {
    onApply(currentJson, loaded && !dirty ? loaded.name : undefined, includeDeleted);
    onClose();
  };
  // Clearing drops the scope with the conditions — there is no filter left for an
  // "include deleted" to widen, and leaving it set would silently carry into the next
  // filter the user builds.
  const clear = () => { onApply('', undefined, false); onClose(); };

  const refreshSaved = () => listFilters().then(setSaved).catch(() => {});

  const save = async () => {
    const name = saveName.trim();
    if (!name) return;
    await saveFilter(name, currentJson);
    setLoaded({ name, filter: currentJson });  // now "on" the saved filter, clean
    setSaveName('');
    refreshSaved();
  };

  const updateExisting = async () => {
    if (!loaded) return;
    await saveFilter(loaded.name, currentJson);  // backend upserts by name
    setLoaded({ name: loaded.name, filter: currentJson });  // back to clean
    refreshSaved();
  };

  const load = (recno: string) => {
    const f = saved.find((x) => String(x.recno) === recno);
    if (!f) return;
    const parsed = parseFilterJson(f.filter);
    setConditions(parsed.length ? parsed : [{ ...EMPTY }]);
    setIdCounter(parsed.length || 1);
    setLoaded({ name: f.name, filter: canon(f.filter) });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim bg-opacity-50 backdrop-blur-sm p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        role="dialog"
        aria-label="Custom Filter"
        className="bg-panel rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
      >
        <ModalHeader
          title={
            <span className="flex items-center gap-2 min-w-0">
              Custom Filter
              {loaded && (
                <span
                  title={dirty ? 'Edited — not yet saved' : 'Saved filter'}
                  className={`text-xs truncate ${dirty ? 'italic text-amber-300' : 'font-medium text-indigo-300'}`}
                >
                  {loaded.name}{dirty ? ' *' : ''}
                </span>
              )}
            </span>
          }
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          actions={
            saved.length > 0 ? (
              <select
                aria-label="Load saved filter"
                className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-gray-700 dark:text-gray-200"
                defaultValue=""
                onChange={(e) => load(e.target.value)}
              >
                <option value="">Load saved filter…</option>
                {saved.map((f) => <option key={f.recno} value={f.recno}>{f.name}</option>)}
              </select>
            ) : undefined
          }
        />

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-label">Filter Conditions</label>
            {/* An empty picker with no explanation reads as a broken page and
                lands as a support ticket — the usual cause is a missing endpoint
                grant (the field fetch 403s and resolves empty). Name it. */}
            {!loadingFields && filterableFieldDefs.length === 0 && (
              <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                No filter fields available — ask an admin for access.
              </p>
            )}
            {conditions.map((condition) => (
              <FilterConditionRow
                key={condition.id}
                condition={condition}
                fieldDefinitions={filterableFieldDefs}
                loadingFields={loadingFields}
                onUpdate={updateCondition}
                onRemove={removeCondition}
                removeDisabled={conditions.length === 1}
              />
            ))}
            {/* Full-width Add Filter under the last condition so users can keep
                adding rows without moving the cursor back up to the header.
                Stops at MAX_CONDITIONS: past it the server drops the extra rows. */}
            <button
              type="button"
              onClick={addCondition}
              disabled={atCap}
              className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 border border-dashed border-indigo-300 dark:border-indigo-700 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-indigo-50 dark:disabled:hover:bg-indigo-900/20"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Filter
            </button>
            {atCap && (
              <p className="text-center text-xs text-muted">
                Maximum {MAX_CONDITIONS} conditions
              </p>
            )}
          </div>
        </div>

        {/* Scope widener, above the footer so it reads as part of what Apply will
            run rather than as another action button. Only for a scope whose default
            excludes soft-deleted records (Templates); Quotes has no such notion.
            Mirrors the global order Custom Filter's own Include Deleted toggle. */}
        {showIncludeDeleted && (
          <div className="px-4 pt-3">
            <label className="flex items-center gap-1.5 text-[0.75rem] font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <Toggle
                size="sm"
                checked={includeDeleted}
                onChange={setIncludeDeleted}
                ariaLabel="Include deleted templates"
              />
              Include Deleted
            </label>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1.5">
            {loaded && dirty && (
              <button
                type="button"
                onClick={updateExisting}
                title={`Overwrite the saved filter “${loaded.name}”`}
                className="px-2.5 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded"
              >
                Update “{loaded.name}”
              </button>
            )}
            <input
              aria-label="Save filter name"
              className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-gray-700 dark:text-gray-200"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="Save as…"
            />
            <button
              type="button"
              onClick={save}
              disabled={!saveName.trim()}
              className="px-2.5 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-40"
            >
              Save As
            </button>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded">Cancel</button>
            <button type="button" onClick={clear} className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">Clear Filter</button>
            <button type="button" onClick={apply} className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded">Apply Filter</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomFilterDialogBase;
